import React, { useEffect, useState } from 'react';
import './DataTableStagesList.scss';
import BootstrapTable, {
    TableChangeState,
    TableChangeType,
} from '../../../components/react-bootstrap-table-next/react-bootstrap-table2';
import moment from 'moment';
import SortingOption from '../../../components/Table/models/bootstrap-table-sorting-option';
import {
    DataRequestProject,
    DataRequestProjectState,
} from '../../../models/DataRequestHub/DataRequestProject';
import { Badge, Button } from 'reactstrap';
import {
    DataTable,
    DataTableAndTableViewFlatRow,
    DataTableView,
    DataTableViewProgressStatus,
    TableRowType,
    TableViewProgressStatusList,
} from '../../../models/DataRequestHub/DataTable';
import CreateDataTablePopup from '../../Admin/DataRequest/Popups/CreateDataTablePopup/CreateDataTablePopup';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWrench } from '@fortawesome/free-solid-svg-icons';
import Helpers from '../../../utils/helper';
import EditableDateLabel from '../../../components/EditableDateLabel/EditableDateLabel';
import { isDateInvalid } from '../../../components/EditableTable/EditableTableValidationHelper';
import createNotification from '../../../utils/createNotification';
import { useStateSelector } from '../../../store/selectors';
import { faPlusSquare } from '@fortawesome/free-regular-svg-icons';
import { DesignModeSwitcher } from '../DesignModeSwitcher/DesignModeSwitcher';
import { useNavigate } from 'react-router-dom';
import { DataFormStageNameEditor } from '../QuestionnaireStagesList/QuestionnaireStageNameEditor/DataFormStageNameEditor';
import { EditorTypeEnum } from '../../../models/DataRequestHub/ProjectEditorEnum';
import { TableNotificationStatus } from '../../../models/DataRequestHub/TableNotificationStatus';
import ReactSwitch from 'react-switch';
import MemberAccessPopup from '../MemberAccessPopup/MemberAccessPopup';
import InfoTooltip from '../../../components/info-tooltip/info-tooltip';
import {
    dataRequestItemAccessRightColumnTooltipText,
    MemberAccess,
} from '../../../models/DataRequestHub/MemberAccess';
import MembersAccessLockerButton from '../MembersAccessLockerButton/MembersAccessLockerButton';
import ReactDOMServer from 'react-dom/server';
import { useDataRequestUrl } from '../../../hooks/useDataRequestDashboardUrl';
import { useIsDataRequestProjectReadonly } from '../../../custom-hooks/useIsDataRequestProjectReadonly';

interface DataTableStagesListProps {
    projects: DataRequestProject[];
    isAdminView: boolean;
    isEditorMode: boolean;
    invalidTableStageIds: number[];
    invalidTableViewIds: number[];
    updateProjectsData(isInitialFetch?: boolean): void;
    isTemplateEditorView: boolean;
    openSettings?(): void;
    onAddNewDataTableViewClick?(tableId: number): void;
    onTableViewNameClick?(tableId: number, viewId: number): void;
    onTableNameClick?(tableId: number): void;
    onArchiveClick?(tableId: number, viewId: number): void;
    enableDesignMode?(tableId: number): void;
    areTableNamesClickable?: boolean;
}

const DataTableStagesList = (props: DataTableStagesListProps) => {
    const navigate = useNavigate();
    const axios = useStateSelector((s) => s.core.axios);
    const [tableData, setTableData] = useState<DataTableAndTableViewFlatRow[]>(
        []
    );
    const [selectedViewId, setSelectedViewId] = useState(0);
    const [sortingOption, setSortingOption] = useState<SortingOption>({
        dataField: 'dateCreated',
        order: 'desc',
    });
    const [isCreateDataTablePopupVisible, setIsCreateDataTablePopupVisible] =
        useState(false);
    const [isMemberAccessPopupVisible, setIsMemberAccessPopupVisible] =
        useState(false);
    const tablesNotificationStatus = useStateSelector(
        (s) => s.dataRequestsNotifications.tablesNotificationsStatus
    );
    const currentUser = useStateSelector((s) => s.currentUser);
    const isReadonlyProject = useIsDataRequestProjectReadonly(props.projects);
    const dashboardPath = useDataRequestUrl();

    const getTableNotificationStatus = (
        tableId: number
    ): TableNotificationStatus => {
        const targetTableNotificationStatus = tablesNotificationStatus?.find(
            (f) => f.tableId === tableId
        );

        return targetTableNotificationStatus;
    };

    const getProjectId = (tableId: number) => {
        const projectId = props.projects.find((project) =>
            project.dataTables.some((table) => table.id === tableId)
        ).id;

        return projectId;
    };

    const handleDueDateChange = (
        date: Date,
        tableId: number,
        viewId: number
    ) => {
        if (isDateInvalid(date)) {
            date = new Date('0001-01-01');
        }

        const params = {
            dueDate: date,
        };

        const projectId = getProjectId(tableId);
        axios
            .put(
                `/api/dataRequestProjects/${projectId}/dataTables/${tableId}/dataTableViews/${viewId}/dueDate`,
                params
            )
            .then((response) => {
                if (response.status === 200) {
                    props.updateProjectsData();
                } else {
                    const message = (response as any)?.response?.data?.detail;
                    createNotification(
                        message ?? 'An error occured while updating stage',
                        'error'
                    );
                }
            });
    };

    const isAccessRightsColumnVisible = () => {
        const ownerIds = props.projects.map((project) => project.userId);
        const isOwnerAccess = ownerIds.every(
            (ownerId) => ownerId === currentUser?.user?.id
        );
        const doAffiliatedMembersExist =
            props.projects.flatMap((project) => project.affiliatedMembers)
                ?.length > 0;
        return (isOwnerAccess || props.isAdminView) && doAffiliatedMembersExist;
    };

    const getColumns = () => [
        { dataField: 'id', text: '', hidden: true },
        {
            dataField: 'practiceName',
            text: 'Practice Name',
            hidden: props.projects?.length <= 1,
            sort: true,
            formatter: (
                cellContent: string,
                row: DataTableAndTableViewFlatRow
            ) => (row.rowType === TableRowType.DataTable ? cellContent : ''),
        },
        {
            dataField: 'name',
            text: 'Table Views',
            sort: true,
            headerClasses: 'stage-name-header',
            formatter: (
                cellContent: string,
                row: DataTableAndTableViewFlatRow
            ) => {
                const projectId = getProjectId(row.tableId);
                const updateUrl =
                    row.rowType === TableRowType.DataTable
                        ? `/api/DataRequestProjects/${projectId}/dataTables/${row.tableId}/name`
                        : `/api/DataRequestProjects/${projectId}/dataTables/${row.tableId}/dataTableViews/${row.viewId}/name`;

                const tableNotificationState = getTableNotificationStatus(
                    row.tableId
                );
                let notificationsCount = 0;

                if (
                    row.rowType === TableRowType.DataTable &&
                    tableNotificationState?.isNew &&
                    !tableNotificationState?.isNewProject &&
                    !props.isAdminView
                ) {
                    notificationsCount =
                        tableNotificationState?.notificationsCount ?? 0;
                }

                if (
                    row.rowType === TableRowType.DataTableView &&
                    !props.isAdminView
                ) {
                    notificationsCount =
                        tableNotificationState?.viewsNotificationsStatus.find(
                            (viewState) => viewState.viewId === row.viewId
                        )?.notificationsCount ?? 0;
                }

                const isNameClickable =
                    props.isAdminView ||
                    (row.rowType === TableRowType.DataTableView &&
                        row.progressStatus !==
                            DataTableViewProgressStatus.Complete);
                return (
                    <div className="data-table-name-wrapper">
                        <DataFormStageNameEditor
                            isNameClickable={
                                isNameClickable || props.areTableNamesClickable
                            }
                            updateUrl={updateUrl}
                            onSuccessfulUpdate={() => {
                                props.updateProjectsData();
                            }}
                            stageName={cellContent}
                            onStageNameClick={() => {
                                row.rowType === TableRowType.DataTableView
                                    ? props.onTableViewNameClick(
                                          row.tableId,
                                          row.viewId
                                      )
                                    : props.onTableNameClick(row.tableId);
                            }}
                            isTemplateEditorView={props.isEditorMode}
                            isEditable={
                                props.isAdminView &&
                                isProjectActive() &&
                                !isReadonlyProject
                            }
                            enableLinkText={
                                props.isAdminView ||
                                row.rowType === TableRowType.DataTableView
                            }
                        />
                        {notificationsCount > 0 && !props.isEditorMode ? (
                            <Badge className="unread-comments-badge">
                                {notificationsCount > 9
                                    ? '9+'
                                    : notificationsCount}
                            </Badge>
                        ) : (
                            <></>
                        )}
                    </div>
                );
            },
        },
        {
            dataField: 'id',
            text: '',
            sort: false,
            hidden: props.isTemplateEditorView,
            formatter: (_: number, row: DataTableAndTableViewFlatRow) => {
                return row.rowType === TableRowType.DataTableView ? (
                    <div
                        className="link-text view-archive-link"
                        onClick={() => {
                            props.onArchiveClick(row.tableId, row.viewId);
                        }}>
                        {' '}
                        View Archive
                    </div>
                ) : (
                    <></>
                );
            },
        },
        {
            dataField: 'progressStatus',
            text: 'Status',
            sort: true,
            formatter: (
                cellContent: DataTableViewProgressStatus,
                row: DataTableAndTableViewFlatRow
            ) =>
                row.rowType === TableRowType.DataTableView ? (
                    <div
                        style={{
                            color: TableViewProgressStatusList.find(
                                (x) => x.id == (cellContent ?? 0)
                            ).color,
                            fontFamily: 'SegoeUI-Bold',
                        }}>
                        {
                            TableViewProgressStatusList.find(
                                (x) => x.id == (cellContent ?? 0)
                            ).name
                        }
                    </div>
                ) : (
                    <></>
                ),
        },
        {
            dataField: 'dateCreated',
            text: 'Date Created',
            sort: true,
            formatter: (cellContent: Date, row: DataTableAndTableViewFlatRow) =>
                row.rowType === TableRowType.DataTableView
                    ? moment(cellContent).format('L')
                    : '',
        },
        {
            dataField: 'dueDate',
            text: 'Due Date',
            sort: true,
            formatter: (
                cellContent: Date,
                row: DataTableAndTableViewFlatRow
            ) => {
                return row.rowType === TableRowType.DataTableView ? (
                    <EditableDateLabel
                        value={cellContent}
                        isEditable={props.isAdminView && !isReadonlyProject}
                        onChange={(value) =>
                            handleDueDateChange(
                                new Date(value),
                                row.tableId,
                                row.viewId
                            )
                        }
                    />
                ) : (
                    <></>
                );
            },
        },
        {
            dataField: 'dateComplete',
            text: 'Date Complete',
            sort: true,
            formatter: (cellContent: Date) =>
                cellContent ? moment(cellContent).format('L') : 'NA',
            hidden: true,
        },
        {
            dataField: 'id',
            text: 'Action',
            sort: false,
            formatter: (_: number, row: DataTableAndTableViewFlatRow) => {
                return row.rowType === TableRowType.DataTable ? (
                    <div className="action-icons">
                        <FontAwesomeIcon
                            onClick={() =>
                                props.onAddNewDataTableViewClick(row.tableId)
                            }
                            icon={faPlusSquare as any}
                        />
                    </div>
                ) : (
                    <></>
                );
            },
            hidden: !props.isTemplateEditorView || !props.isAdminView,
        },
        {
            dataField: 'isVisible',
            text: 'Visible',
            hidden: !props.isAdminView,
            sort: false,
            formatter: (
                cellContent: boolean,
                row: DataTableAndTableViewFlatRow
            ) => (
                <ReactSwitch
                    offColor="#d92550"
                    checked={cellContent}
                    onChange={(event) =>
                        updateTableVisibility(event, row.tableId, row.viewId)
                    }
                    height={22}
                    disabled={isReadonlyProject}></ReactSwitch>
            ),
        },
        {
            dataField: 'membersAccessList',
            text: 'Access Rights',
            headerClasses: 'member-access-rights-column',
            hidden: !isAccessRightsColumnVisible(),
            headerFormatter: () => {
                return (
                    <div className="column-header-tooltip-wrapper">
                        {'Access Rights'}
                        <InfoTooltip
                            text={''}
                            html={ReactDOMServer.renderToString(
                                dataRequestItemAccessRightColumnTooltipText
                            )}
                        />
                    </div>
                );
            },
            formatter: (_: any, row: DataTableAndTableViewFlatRow) =>
                row.rowType === TableRowType.DataTableView ? (
                    <div className="lock-icon-wrapper">
                        <MembersAccessLockerButton
                            membersAccessList={row.membersAccessList}
                            onClick={() => {
                                if (
                                    row.rowType === TableRowType.DataTableView
                                ) {
                                    setIsMemberAccessPopupVisible(true);
                                    setSelectedViewId(row.viewId);
                                }
                            }}
                        />
                    </div>
                ) : (
                    <></>
                ),
        },
    ];

    const updateTableVisibility = (
        isVisible: boolean,
        tableId: number,
        viewId: number
    ) => {
        const projectId = getProjectId(tableId);

        const params = {
            tableId: tableId ?? 0,
            tableViewId: viewId ?? 0,
            isVisible: isVisible,
        };

        axios
            .put(
                `/api/DataRequestProjects/${projectId}/dataTableVisibleStatuses`,
                [params]
            )
            .then((response) => {
                if (response.status === 200) {
                    props.updateProjectsData();
                } else {
                    const message = (response as any)?.response?.data?.detail;
                    createNotification(
                        message ?? 'An error occured while updating stage',
                        'error'
                    );
                }
            });
    };

    const sortDataTables = (newSortingOption: SortingOption): DataTable[] => {
        const dataTables = getDataTables();

        const tablesWithSortedViews = dataTables.map((table): DataTable => {
            switch (newSortingOption.dataField) {
                case 'dueDate': {
                    const noDueDateViews = table.views?.filter(
                        (f) => f.dueDate === null
                    );
                    const viewsWithDueDate = table.views?.filter(
                        (f) => f.dueDate !== null
                    );
                    const sortedViews = Helpers.dynamicSorting(
                        viewsWithDueDate,
                        newSortingOption.dataField,
                        newSortingOption.order
                    );

                    return {
                        ...table,
                        views: [...sortedViews, ...noDueDateViews],
                    };
                }
                default: {
                    const sortedViews = Helpers.dynamicSorting(
                        table.views,
                        newSortingOption.dataField,
                        newSortingOption.order
                    );

                    return {
                        ...table,
                        views: [...sortedViews],
                    };
                }
            }
        });

        switch (newSortingOption.dataField) {
            case 'dueDate':
                const dueDateTables = tablesWithSortedViews.filter((f) =>
                    f.views?.every((e) => e.dueDate === null)
                );

                const tableWithViews = tablesWithSortedViews.filter((f) =>
                    f.views?.some((e) => e.dueDate)
                );

                const result = sortDataTablesByDueDate(
                    tableWithViews,
                    sortingOption.order
                );

                return [...result, ...dueDateTables];

            default:
                let sortedDataTables: DataTable[] = Helpers.dynamicSorting(
                    tablesWithSortedViews,
                    newSortingOption.dataField,
                    newSortingOption.order
                );
                return sortedDataTables;
        }
    };

    const sortDataTablesByDueDate = (
        dataTables: DataTable[],
        order: 'asc' | 'desc'
    ) => {
        const result = dataTables.sort((a, b) => {
            const directionScale = order === 'asc' ? 1 : -1;

            const aNotEmptyDates = a.views
                .filter((f) => f.dueDate !== null)
                .map((m) => new Date(m.dueDate).getTime());

            const aDueDate = aNotEmptyDates.reduce(
                (max, current) => (current > max ? current : max),
                aNotEmptyDates[0]
            );

            const bNotEmptyDates = b.views
                .filter((f) => f.dueDate !== null)
                .map((m) => new Date(m.dueDate).getTime());

            const bDueDate = bNotEmptyDates.reduce(
                (max, current) => (current > max ? current : max),
                bNotEmptyDates[0]
            );

            if (aDueDate === bDueDate) {
                return 0;
            }

            return aDueDate > bDueDate ? directionScale : -directionScale;
        });

        return result;
    };

    useEffect(() => {
        const sortedDataTables = sortDataTables(sortingOption);
        const tableRows = sortedDataTables.flatMap(
            (table: DataTable): DataTableAndTableViewFlatRow[] => {
                const parentTable: DataTableAndTableViewFlatRow = {
                    rowId: `Table-${table.id}`,
                    tableId: table.id,
                    viewId: 0,
                    name: table.name,
                    dateCreated: table.dateCreated,
                    rowType: TableRowType.DataTable,
                    practiceName: table.practiceName,
                    isVisible: table.isVisible,
                    membersAccessList: [],
                };

                const viewsList = table.views.map(
                    (view: DataTableView): DataTableAndTableViewFlatRow => {
                        const result: DataTableAndTableViewFlatRow = {
                            rowId: `View-${view.id}`,
                            tableId: table.id,
                            viewId: view.id,
                            name: view.name,
                            dateCreated: view.dateCreated,
                            description: view.description,
                            rowType: TableRowType.DataTableView,
                            dueDate: view.dueDate,
                            progressStatus: view.progressStatus,
                            isVisible: view.isVisible,
                            membersAccessList: view.membersAccessList,
                        };
                        return result;
                    }
                );
                if (!props.isAdminView && viewsList.length === 0) {
                    return [];
                }
                return [parentTable, ...viewsList];
            }
        );

        setTableData(tableRows);
    }, [props.projects, sortingOption]);

    const getProject = () => {
        return props.projects.find((f) => f);
    };

    const getDataTables = () =>
        props.projects.flatMap((project) =>
            project.dataTables.map((table) => ({
                ...table,
                practiceName: project.practiceName,
            }))
        );

    const handleTableChange = (
        _type: TableChangeType,
        newState: TableChangeState<any>
    ) => {
        const newSortingOption: SortingOption = {
            dataField: newState.sortField,
            order: newState.sortOrder,
        };

        setSortingOption(newSortingOption);
    };

    const isProjectActive = () => {
        const targetProject = props.projects.find((project) => project);
        return (
            targetProject.state === DataRequestProjectState.Active &&
            !isReadonlyProject
        );
    };

    const getViewMembersAccessList = (viewId: number): MemberAccess[] => {
        const allViwes = props.projects?.flatMap((project) =>
            project.dataTables.flatMap((table) => table.views)
        );
        const targetView = allViwes.find((view) => view.id === viewId);

        return targetView.membersAccessList;
    };

    const getUpdateMembersAccessUrl = (viewId: number): string => {
        const targetTable = props.projects
            ?.flatMap((project) => project.dataTables)
            ?.find((dataTable) =>
                dataTable.views.some((view) => view.id === viewId)
            );

        const projectId =
            props.projects?.find((project) =>
                project?.dataTables.some((table) => table.id == targetTable?.id)
            ).id ?? 0;

        return `/api/DataRequestProjects/${projectId}/dataTables/${targetTable.id}/tableViews/${viewId}/membersAccess`;
    };

    return (
        <div className="data-table-stages-list">
            <div className="editor-header">
                {props.isAdminView ? (
                    <div className="data-table-stages-host-header">
                        <Button
                            className="btn btn-primary"
                            onClick={() => {
                                setIsCreateDataTablePopupVisible(true);
                            }}
                            disabled={isReadonlyProject}>
                            Create Additional Table
                        </Button>
                        <div className="project-settings-section">
                            {props.isTemplateEditorView && props.isAdminView ? (
                                <div
                                    className="clickable"
                                    onClick={() => {
                                        props.openSettings();
                                    }}>
                                    <FontAwesomeIcon icon={faWrench} />
                                    {'User View Settings'}
                                </div>
                            ) : (
                                <></>
                            )}
                            {isProjectActive() && (
                                <DesignModeSwitcher
                                    defaultValue={
                                        props.isEditorMode ? true : false
                                    }
                                    onEnable={props.enableDesignMode}
                                    onDisable={() => {
                                        navigate(
                                            `/${dashboardPath}?tab=${
                                                EditorTypeEnum.DataTable + 1
                                            }`,
                                            {
                                                state: {
                                                    projectId: getProject().id,
                                                    isAdmin: true,
                                                    projectName:
                                                        getProject().name,
                                                    practiceName:
                                                        getProject()
                                                            .practiceName,
                                                },
                                            }
                                        );
                                    }}
                                    formId={0}></DesignModeSwitcher>
                            )}
                        </div>
                    </div>
                ) : (
                    []
                )}
            </div>

            <BootstrapTable
                striped
                bordered
                bootstrap4
                remote
                keyField="rowId"
                defaultSorted={[sortingOption]}
                data={tableData}
                columns={getColumns()}
                rowClasses={(row: DataTableAndTableViewFlatRow) => {
                    if (row.rowType === TableRowType.DataTable) {
                        const invalidClasses =
                            props.invalidTableStageIds.includes(row.tableId)
                                ? 'invalid-stage'
                                : '';
                        return `data-table-row ${invalidClasses}`;
                    } else {
                        const invalidClasses =
                            props.invalidTableViewIds.includes(row.viewId)
                                ? 'invalid-stage'
                                : '';
                        return `table-view-row ${invalidClasses}`;
                    }
                }}
                onTableChange={handleTableChange}
                noDataIndication="No Data available"
            />

            {isCreateDataTablePopupVisible && props.isAdminView ? (
                <CreateDataTablePopup
                    project={getProject()}
                    onClose={() => setIsCreateDataTablePopupVisible(false)}
                    onAdd={props.updateProjectsData}
                />
            ) : (
                []
            )}

            {isMemberAccessPopupVisible ? (
                <MemberAccessPopup
                    memberAccessList={getViewMembersAccessList(selectedViewId)}
                    updateUrl={getUpdateMembersAccessUrl(selectedViewId)}
                    closePopup={() => setIsMemberAccessPopupVisible(false)}
                    onSuccessfulUpdate={props.updateProjectsData}
                />
            ) : (
                []
            )}
        </div>
    );
};

export default DataTableStagesList;
